// ═══════════════════════════════════════════════════════════════════════════
// core/lib/accionAuth — la pantalla propia de los links de mail de Firebase
// Auth (`/auth/accion`) y la contraseña nueva (Plataforma A3, 25-sep-2026)
// ═══════════════════════════════════════════════════════════════════════════
// Regla de Mariano (25-sep): «siempre hay que verificar mails». Los links que
// manda Firebase Auth (verificar el mail, restablecer la contraseña, deshacer
// un cambio de mail) llegan a NUESTRA pantalla (Firebase Console → Templates →
// «Customize action URL», runbooks/auth-acciones-de-mail.md) con
// `?mode=…&oobCode=…&continueUrl=…`. Lo que se decide acá es puro:
//   · qué modos se atienden — lista CERRADA; un modo desconocido o un link sin
//     `oobCode` falla CERRADO (no se intenta nada contra Auth);
//   · qué se le dice a la persona por cada código de error de Auth;
//   · la URL sin el `oobCode` (se borra apenas se lee: no queda en el
//     historial ni viaja en un Referer);
//   · el largo mínimo ÚNICO de una contraseña nueva (registro, setup, canje de
//     invitación y reset): 8.
// El destino de `continueUrl` lo valida `rutaDeContinueUrl` en `rutas.js`,
// junto al resto de las reglas de "adónde se vuelve".
// ═══════════════════════════════════════════════════════════════════════════

/** Los modos de los links de Firebase Auth que esta pantalla atiende. Lista cerrada. */
export const MODOS_ACCION_AUTH = Object.freeze(['resetPassword', 'verifyEmail', 'recoverEmail'])

/** El largo mínimo de una contraseña NUEVA, en todos lados (registro, setup, canje, reset). */
export const LARGO_MINIMO_PASSWORD = 8

/**
 * PURO. Lee los parámetros del link. `valida` es false si el modo no está en
 * la lista cerrada o falta el `oobCode`: con eso la pantalla no toca Auth.
 * @param {string} search   `location.search` (con o sin `?`)
 * @returns {{ modo: string|null, oobCode: string|null, continueUrl: string|null, valida: boolean, motivo: string|null }}
 */
export function leerAccionAuth(search) {
  const p = new URLSearchParams(typeof search === 'string' ? search : '')
  const modo = p.get('mode') || null
  const oobCode = (p.get('oobCode') || '').trim() || null
  const continueUrl = p.get('continueUrl') || null
  let motivo = null
  if (!oobCode) motivo = 'sin-codigo'
  else if (!MODOS_ACCION_AUTH.includes(modo)) motivo = 'modo-desconocido'
  return { modo, oobCode, continueUrl, valida: motivo === null, motivo }
}

/**
 * PURO. La misma URL sin `oobCode` (el resto de la query se conserva). Lo que
 * se le pasa a `history.replaceState` apenas se leyó el código.
 * @param {string} pathname
 * @param {string} search
 * @param {string} [hash]
 */
export function urlSinOobCode(pathname, search, hash = '') {
  const p = new URLSearchParams(typeof search === 'string' ? search : '')
  p.delete('oobCode')
  const q = p.toString()
  return `${pathname}${q ? `?${q}` : ''}${hash || ''}`
}

const MENSAJES_ACCION_AUTH = Object.freeze({
  'sin-codigo': 'El link está incompleto. Abrilo de nuevo desde el mail, o pedí uno nuevo.',
  'modo-desconocido': 'Este link no es válido. Abrilo de nuevo desde el mail, o pedí uno nuevo.',
  'auth/expired-action-code': 'El link venció. Pedí uno nuevo desde la pantalla de inicio de sesión.',
  'auth/invalid-action-code': 'El link ya se usó o no es válido. Pedí uno nuevo desde la pantalla de inicio de sesión.',
  'auth/user-disabled': 'La cuenta está deshabilitada. Contactá al administrador.',
  'auth/user-not-found': 'El link ya no es válido. Pedí uno nuevo desde la pantalla de inicio de sesión.',
  'auth/weak-password': `La contraseña es muy débil. Usá al menos ${LARGO_MINIMO_PASSWORD} caracteres.`,
  'auth/network-request-failed': 'Hubo un problema de conexión. Revisá tu internet e intentá de nuevo.',
  'auth/too-many-requests': 'Demasiados intentos. Esperá unos minutos antes de volver a intentar.',
})

/**
 * PURO. El mensaje para la persona según el código (de Auth o propio).
 * Uno no mapeado deja el código crudo a la vista, como `mensajeAuthError`:
 * quien reporta el problema lo puede leer de la pantalla.
 * @param {unknown} code
 */
export function mensajeAccionAuth(code) {
  if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(MENSAJES_ACCION_AUTH, code)) return MENSAJES_ACCION_AUTH[code]
  return typeof code === 'string' && code
    ? `No pudimos completar la acción (${code}).`
    : 'No pudimos completar la acción. Pedí un link nuevo desde la pantalla de inicio de sesión.'
}

/**
 * PURO. El error de una contraseña NUEVA, o null si sirve. Sin `confirmacion`
 * (el canje de invitación no la pide) sólo se mira el largo.
 * @param {unknown} password
 * @param {unknown} [confirmacion]
 */
export function errorPasswordNueva(password, confirmacion) {
  if (typeof password !== 'string' || password.length < LARGO_MINIMO_PASSWORD) {
    return `La contraseña debe tener al menos ${LARGO_MINIMO_PASSWORD} caracteres.`
  }
  // Los dos valores los tipea la MISMA persona en su navegador (mismo motivo
  // que SEC-SAST.3 en RegistroPage): no hay secreto ajeno que medir.
  if (confirmacion !== undefined && password !== confirmacion) return 'Las contraseñas no coinciden.' // nosem: ajinabraham.njsscan.crypto.timing_attack_node.node_timing_attack
  return null
}
